import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

// Contract expected from the job handler (part 122):
//   GET  /v1/jobs?status=&limit=      -> Job[]
//   GET  /v1/jobs/stats               -> JobStats
//   POST /v1/jobs/{id}/requeue        -> Job
// The queue is drained by app.JobWorker; this screen only observes and requeues.

type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'dead'

interface Job {
  id: string
  tenant_id: string
  kind: string
  payload: Record<string, unknown>
  status: JobStatus
  attempts: number
  max_attempts: number
  run_at: string
  locked_at?: string
  last_error?: string
  created_at: string
  finished_at?: string
}

interface JobStats {
  queued: number
  running: number
  failed: number
  dead: number
  succeeded: number
  total: number
}

const kindLabel: Record<string, string> = {
  'report.render': 'สร้างรายงาน',
  'webhook.retry': 'ส่งเว็บฮุกซ้ำ',
  'extraction.retry': 'สกัดข้อความซ้ำ',
  'archive.run': 'จัดเก็บถาวร',
}

const statusLabel: Record<JobStatus, string> = {
  queued: 'รอคิว',
  running: 'กำลังทำงาน',
  succeeded: 'สำเร็จ',
  failed: 'รอลองใหม่',
  dead: 'ล้มเหลวถาวร',
}

const statusHint: Record<JobStatus, string> = {
  queued: 'ยังไม่เคยรัน หรือถูกจัดคิวใหม่',
  running: 'ผู้ปฏิบัติงานถือสิทธิ์อยู่',
  succeeded: 'ทำงานเสร็จสมบูรณ์',
  failed: 'ลองใหม่ตามเวลาถอยหลัง',
  dead: 'ครบจำนวนครั้งแล้ว ต้องกด requeue',
}

const statusTone: Record<JobStatus, string> = {
  queued: 'var(--color-ivory-dim)',
  running: 'var(--color-gold)',
  succeeded: 'var(--color-emerald-soft)',
  failed: 'var(--color-amber)',
  dead: 'var(--color-critical)',
}

const filters: { value: '' | JobStatus; label: string }[] = [
  { value: '', label: 'ทุกสถานะ' },
  { value: 'queued', label: statusLabel.queued },
  { value: 'running', label: statusLabel.running },
  { value: 'failed', label: statusLabel.failed },
  { value: 'dead', label: statusLabel.dead },
  { value: 'succeeded', label: statusLabel.succeeded },
]

function isTerminal(status: JobStatus) {
  return status === 'succeeded' || status === 'dead'
}

function scheduleLabel(job: Job): { text: string; tone?: string } {
  if (job.status === 'running') return { text: 'กำลังทำงาน', tone: 'var(--color-gold)' }
  if (isTerminal(job.status)) {
    return { text: job.finished_at ? `เสร็จ ${thaiDateTime(job.finished_at)}` : '—' }
  }
  const due = Date.parse(job.run_at)
  if (!Number.isNaN(due) && due <= Date.now()) {
    return { text: 'พร้อมทำงาน', tone: 'var(--color-gold)' }
  }
  return { text: thaiDateTime(job.run_at) }
}

function truncate(text: string, max = 90): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

export function Jobs() {
  const qc = useQueryClient()
  const [status, setStatus] = useState<'' | JobStatus>('')

  const stats = useQuery({
    queryKey: ['job-stats'],
    queryFn: () => api<JobStats>('/v1/jobs/stats'),
    refetchInterval: 10_000,
    staleTime: 5_000,
  })

  const q = useQuery({
    queryKey: ['jobs', status],
    queryFn: () => list<Job>(`/v1/jobs?limit=100${status ? `&status=${status}` : ''}`),
    refetchInterval: 10_000,
    staleTime: 5_000,
  })

  const requeue = useMutation({
    mutationFn: (id: string) => apiWrite<Job>(`/v1/jobs/${id}/requeue`, 'POST', {}),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] })
      void qc.invalidateQueries({ queryKey: ['job-stats'] })
    },
  })

  const head = (
    <PageHead
      title="คิวงานเบื้องหลัง"
      sub="งานที่รันนอกคำขอของผู้ใช้ · ลองใหม่แบบถอยหลัง · งานที่ครบจำนวนครั้งจะถูกแยกไว้ให้ตรวจ"
    />
  )

  if (q.isLoading && stats.isLoading) {
    return (
      <>
        {head}
        <div className="stack"><Skel h={110} /><Skel h={260} /></div>
      </>
    )
  }

  if (q.isError) {
    return (
      <>
        {head}
        <Empty title="โหลดคิวงานไม่ได้" body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์" />
      </>
    )
  }

  const jobs = q.data ?? []
  const s = stats.data
  const total = s?.total ?? jobs.length

  return (
    <>
      {head}

      <div className="grid-4 stagger">
        <Stat label="รอคิว" value={s?.queued ?? 0} unit="งาน"
          foot="ยังไม่ถูกหยิบไปทำ หรือรอถึงเวลาที่กำหนด" />
        <Stat label="กำลังทำงาน" value={s?.running ?? 0} unit="งาน"
          foot="ผู้ปฏิบัติงานถือสิทธิ์อยู่ งานอื่นจะไม่ถูกหยิบซ้ำ" />
        <Stat label="รอลองใหม่" value={s?.failed ?? 0} unit="งาน"
          foot="ล้มเหลวแล้ว ระบบจัดเวลาใหม่ตามเวลาถอยหลัง" />
        <Stat label="ล้มเหลวถาวร" value={s?.dead ?? 0} unit="งาน"
          foot="ครบจำนวนครั้งแล้ว ต้องตรวจสาเหตุแล้วจึง requeue" />
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div className="card-label">รายการงาน</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              ทั้งหมด {total} งาน · สำเร็จแล้ว {s?.succeeded ?? 0} งาน · แสดงไม่เกิน 100 รายการล่าสุด
            </div>
          </div>
          <select
            className="input"
            aria-label="กรองตามสถานะ"
            style={{ marginLeft: 'auto', maxWidth: 220 }}
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | JobStatus)}
          >
            {filters.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        <div className="rule" style={{ margin: '14px 0 0' }} />

        {q.isLoading ? (
          <div style={{ marginTop: 14 }}><Skel h={200} /></div>
        ) : jobs.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty
              title={status ? 'ไม่มีงานในสถานะนี้' : 'ยังไม่มีงานในคิว'}
              body={status
                ? 'ลองเลือกสถานะอื่น หรือรอให้ผู้ปฏิบัติงานหยิบงานจากคิว'
                : 'เมื่อมีงานอย่างการสร้างรายงานหรือส่งเว็บฮุกซ้ำ ระบบจะแสดงรายการที่นี่'}
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>ชนิดงาน</th>
                  <th>สถานะ</th>
                  <th style={{ width: 110 }}>ความพยายาม</th>
                  <th>กำหนดการ</th>
                  <th>ข้อผิดพลาดล่าสุด</th>
                  <th>สร้างเมื่อ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => {
                  const schedule = scheduleLabel(job)
                  const pending = requeue.isPending && requeue.variables === job.id
                  return (
                    <tr key={job.id} style={{ cursor: 'default' }}>
                      <td>
                        <div>{kindLabel[job.kind] ?? job.kind}</div>
                        <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {job.kind} · {job.id.slice(-8)}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`chip${job.status === 'dead' ? '' : job.status === 'succeeded' ? ' gold' : ''}`}
                          style={{
                            whiteSpace: 'nowrap',
                            color: statusTone[job.status],
                            borderColor: 'currentColor',
                          }}
                          title={statusHint[job.status]}
                        >
                          {statusLabel[job.status] ?? job.status}
                        </span>
                      </td>
                      <td className="tnum">{job.attempts} / {job.max_attempts}</td>
                      <td className="dim" style={{ whiteSpace: 'nowrap', color: schedule.tone }}>
                        {schedule.text}
                      </td>
                      <td
                        className="dim"
                        style={{ fontSize: 11.5, maxWidth: 280 }}
                        title={job.last_error ?? ''}
                      >
                        {job.last_error ? truncate(job.last_error) : '—'}
                      </td>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>
                        {thaiDateTime(job.created_at)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {job.status === 'dead' ? (
                          <button
                            className="btn btn-sm"
                            disabled={pending}
                            onClick={() => requeue.mutate(job.id)}
                          >
                            {pending ? 'กำลังคืนคิว…' : 'คืนคิว'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {requeue.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {requeue.error instanceof Error ? requeue.error.message : 'คืนคิวงานไม่สำเร็จ'}
          </div>
        ) : null}
        {requeue.isSuccess ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 12 }}>
            คืนงาน {requeue.data.kind} เข้าคิวแล้ว · เริ่มนับความพยายามใหม่ที่ 0
          </div>
        ) : null}
      </div>

      <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 14 }}>
        งานที่ล้มเหลวจะถูกจัดเวลาใหม่แบบทวีคูณ เริ่มที่ 30 วินาทีและไม่เกิน 1 ชั่วโมง ·
        งานที่ผู้ปฏิบัติงานหยุดกลางทางจะถูกคืนคิวอัตโนมัติเมื่อสิทธิ์ถือครองหมดอายุ ·
        การคืนคิวงานที่ล้มเหลวถาวรจะล้างข้อผิดพลาดเดิมและเริ่มนับจำนวนครั้งใหม่
      </div>
    </>
  )
}
